import { zodResolver } from '@hookform/resolvers/zod';
import { MoveLeft, MoveRight } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { AuthShell } from '@/components/layout/AuthShell';
import { YellowUnderline } from '@/components/patterns/YellowUnderline';

import { MarketingCarousel } from '../components/MarketingCarousel';
import { OnboardingStepper } from '../components/OnboardingStepper';
import { type YourDetailsInput, yourDetailsSchema } from '../schemas/onboardingSchemas';
import { useAuthStore } from '../store/authStore';
import { useOnboardingStore } from '../store/onboardingStore';

// /onboarding/your-details — Step 1 of the 3-screen onboarding flow.
//
// Matches auth-frontend OnboardingDetailsPage: collects firstName, lastName,
// and workspaceName together. No API call — data is held in Zustand and sent
// atomically on the final screen via POST /auth/complete-onboarding.
export function YourDetailsScreen() {
  const navigate = useNavigate();
  const userEmail = useAuthStore((s) => s.user?.email ?? '');
  const draft = useOnboardingStore((s) => s.yourDetails);
  const setDraft = useOnboardingStore((s) => s.setYourDetails);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<YourDetailsInput>({
    resolver: zodResolver(yourDetailsSchema),
    defaultValues: {
      firstName: draft.firstName ?? '',
      lastName: draft.lastName ?? '',
      workspaceName: draft.workspaceName ?? '',
    },
  });

  useEffect(() => {
    const subscription = watch((values) => {
      setDraft({
        firstName: values.firstName ?? '',
        lastName: values.lastName ?? '',
        workspaceName: values.workspaceName ?? '',
      });
    });

    return () => subscription.unsubscribe();
  }, [setDraft, watch]);

  const onSubmit = handleSubmit((values) => {
    setDraft(values);
    navigate('/onboarding/workspace-info');
  });

  return (
    <AuthShell
      marketing={<MarketingCarousel />}
      footer={
        <div className="w-full flex items-center justify-between flex-wrap gap-2">
          <button type="button" disabled className="btn btn-primary-light rounded-full!">
            <MoveLeft className="w-6 h-6 shrink-0" />
            <span>Back</span>
          </button>
          <button type="submit" form="your-details-form" className="btn btn-primary rounded-full!">
            <span>Next</span>
            <MoveRight className="w-6 h-6 shrink-0" />
          </button>
        </div>
      }
    >
      <form
        id="your-details-form"
        onSubmit={onSubmit}
        noValidate
        className="max-w-[460px] w-full shrink-0 flex flex-col gap-4 sm:gap-6 lg:gap-8"
      >
        <OnboardingStepper step={1} />

        <div className="flex flex-col gap-1 sm:gap-2 lg:gap-2.5 text-center">
          <h1 className="text-xl lg:text-2xl lg:leading-[29px] font-bold text-black-light">
            Enter Your <YellowUnderline>Details</YellowUnderline>
          </h1>
          <p className="text-base lg:text-lg leading-[150%] font-medium">
            Enter your information and name your organization to get started
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="first-name" className="sc-input-label" data-required>
                First Name
              </label>
              <input
                id="first-name"
                type="text"
                className="sc-input"
                placeholder="Smith"
                autoComplete="given-name"
                aria-invalid={errors.firstName ? 'true' : undefined}
                {...register('firstName')}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="last-name" className="sc-input-label" data-required>
                Last Name
              </label>
              <input
                id="last-name"
                type="text"
                className="sc-input"
                placeholder="John"
                autoComplete="family-name"
                aria-invalid={errors.lastName ? 'true' : undefined}
                {...register('lastName')}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="email" className="sc-input-label">
              Email
            </label>
            <input
              id="email"
              type="email"
              className="sc-input"
              value={userEmail}
              readOnly
              autoComplete="email"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="workspace-name" className="sc-input-label" data-required>
              Organization Name
            </label>
            <input
              id="workspace-name"
              type="text"
              className="sc-input"
              placeholder="Acme Corp"
              autoComplete="organization"
              aria-invalid={errors.workspaceName ? 'true' : undefined}
              {...register('workspaceName')}
            />
            {errors.workspaceName && (
              <p className="text-sm text-destructive" role="alert">
                {errors.workspaceName.message}
              </p>
            )}
          </div>
        </div>
      </form>
    </AuthShell>
  );
}
